import { DialogComponent } from '@theme/dialog';
import { Scroller } from '@theme/scrolling';
import { KbImageSource } from '@theme/kb-image-source';
import { kbImagePreload } from '@theme/kb-image-preload';

/**
 * @typedef {object} KbMediaVariant
 * @property {string} [type]
 * @property {import('@theme/kb-image-source').KbImageSourceDescriptor|null} [source]
 * @property {string|null} [poster]
 * @property {Array<{ url: string, type: string }>} [sources]
 */

/**
 * @typedef {object} KbShadeMedia
 * @property {KbMediaVariant|null} [desktop]
 * @property {KbMediaVariant|null} [mobile]
 * @property {string} [alt]
 * @property {string|null} [viewport]
 */

/**
 * @typedef {object} KbLightboxDetail
 * @property {string} [source]
 * @property {number} [index]
 * @property {string} [shadeName]
 * @property {HTMLElement|null} [sourceElement]
 */

/**
 * @typedef {HTMLElement & { data?: any, selection?: any }} KbLightboxHost
 */

/**
 * @typedef {{ x: number, y: number }} KbPoint
 */

class KbPdpLightbox extends DialogComponent {
  constructor() {
    super();
    this.selectors = {
      host: 'kb-pdp-product, kb-builder',
      gallery: 'kb-pdp-gallery',
      gallerySlide: '[data-kb-gallery-slide]',
      galleryImage: '.kb-pdp-gallery__image',
      galleryVideo: '[data-kb-gallery-video]',
      galleryVideoToggle: '.kb-pdp-gallery__video-toggle',
      track: '[data-kb-lightbox-track]',
      trackVideo: 'video',
      slide: '[data-kb-lightbox-slide]',
      media: '.kb-pdp-lightbox__media',
      controls: '[data-kb-lightbox-controls]',
      segments: '[data-kb-lightbox-segments]',
      videoToggleTemplate: '[data-kb-lightbox-video-toggle]',
      videoGlyphTemplate: '[data-kb-lightbox-video-glyph]',
    };
    this.classNames = {
      slide: 'kb-pdp-lightbox__slide',
      videoSlide: 'kb-pdp-lightbox__slide--video',
      media: 'kb-pdp-lightbox__media',
      galleryVideoToggle: 'kb-pdp-gallery__video-toggle',
      segment: 'kb-pdp-lightbox__segment',
      videoSegment: 'kb-pdp-lightbox__segment--video',
      segmentLine: 'kb-pdp-lightbox__segment-line',
      segmentFill: 'kb-pdp-lightbox__segment-fill',
      activeSegment: 'is-active',
      videoPaused: 'is-video-paused',
    };
    this.dataAttributes = {
      slide: 'data-kb-lightbox-slide',
      segmentIndex: 'kbLightboxSegment',
      highResolution: 'kbHighResolution',
      dragging: 'dragging',
      zoomed: 'data-zoomed',
      panning: 'data-panning',
      ground: 'data-ground',
    };
    this.cssProperties = {
      zoomScale: '--kb-zoom-scale',
      zoomTranslateX: '--kb-zoom-x',
      zoomTranslateY: '--kb-zoom-y',
    };
    this.eventNames = {
      lightboxOpen: 'kb:lightbox:open',
      dialogOpen: 'dialog:open',
      dialogClose: 'dialog:close',
    };
    this.settings = {
      activeThreshold: 0.5,
      intersectionThresholds: [0.5, 0.75, 1],
      programmaticScrollMs: 700,
      dragThreshold: 10,
      reducedMotionQuery: '(prefers-reduced-motion: reduce)',
      desktopMediaQuery: '(min-width: 990px)',
      zoomEnabled: true,
      zoomLevel: 2,
      zoomMax: 4,
      zoomTrigger: 'double',
      doubleTapDelayMs: 300,
      doubleTapDistance: 50,
      openDurationMs: 450,
      openEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    };
    /**
     * @type {{
     *   index: number,
     *   count: number,
     *   pendingIndex: number,
     *   programmaticIndex: number|null,
     *   reducedMotion: boolean,
     *   zoomed: boolean,
     *   scale: number,
     *   translate: KbPoint
     * }}
     */
    this.state = {
      index: 0,
      count: 0,
      pendingIndex: 0,
      programmaticIndex: null,
      reducedMotion: false,
      zoomed: false,
      scale: 1,
      translate: { x: 0, y: 0 },
    };
    this.touch = {
      initialDistance: 0,
      startScale: 1,
      lastTapTime: 0,
      /** @type {KbPoint|null} */
      lastTapPosition: null,
      panning: false,
      moved: false,
      gestureStart: { x: 0, y: 0 },
      gestureStartTime: 0,
      startPosition: { x: 0, y: 0 },
      startTranslate: { x: 0, y: 0 },
    };
    this.scrollSettleTimer = 0;
    this.dragging = false;
    this.suppressClick = false;
    this.viaTouch = false;
    /** @type {DOMRect|null} */
    this.zoomSourceRect = null;
    /** @type {HTMLElement|null} */
    this.zoomSourceElement = null;
    this.isClosing = false;
    /** @type {MutationObserver|null} */
    this.closeObserver = null;
    /** @type {KbLightboxHost|null} */
    this.host = null;
    /** @type {HTMLElement|null} */
    this.track = null;
    /** @type {HTMLElement|null} */
    this.controls = null;
    /** @type {HTMLElement|null} */
    this.segmentsContainer = null;
    /** @type {HTMLElement[]} */
    this.segments = [];
    /** @type {Scroller|null} */
    this.scroller = null;
    /** @type {IntersectionObserver|null} */
    this.slideObserver = null;
    this.lifetimeAbortController = new AbortController();
    this.openAbortController = new AbortController();
    this.imageSource = new KbImageSource();
  }

  connectedCallback() {
    super.connectedCallback();

    this.state.reducedMotion = window.matchMedia(this.settings.reducedMotionQuery).matches;
    this.readZoomSettings();
    this.host = /** @type {KbLightboxHost|null} */ (this.closest(this.selectors.host));
    this.track = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.track));
    this.controls = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.controls));
    this.segmentsContainer = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.segments));

    const { signal } = this.lifetimeAbortController;
    const openTarget = this.host || document;
    openTarget.addEventListener(this.eventNames.lightboxOpen, this.handleLightboxOpen, { signal });
    this.addEventListener(this.eventNames.dialogOpen, this.handleDialogOpen, { signal });
    this.addEventListener(this.eventNames.dialogClose, this.handleDialogClose, { signal });
    if (this.segmentsContainer) {
      this.segmentsContainer.addEventListener('click', this.handleSegmentsClick, { signal });
    }
    this.setupDrag();

    this.style.setProperty('--kb-pdp-lightbox-motion-duration', `${this.settings.openDurationMs}ms`);

    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLElement) {
      this.closeObserver = new MutationObserver(() => this.handleClosingState());
      this.closeObserver.observe(dialog, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.lifetimeAbortController.abort();
    this.openAbortController.abort();
    if (this.slideObserver) this.slideObserver.disconnect();
    if (this.closeObserver) this.closeObserver.disconnect();
    if (this.scroller) this.scroller.destroy();
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
  }

  readZoomSettings() {
    this.settings.zoomEnabled = this.dataset.zoomEnabled !== 'false';
    const level = Number(this.dataset.zoomLevel);
    if (!Number.isNaN(level) && level > 1) this.settings.zoomLevel = Math.min(level, this.settings.zoomMax);
    this.settings.zoomTrigger = this.dataset.zoomTrigger === 'single' ? 'single' : 'double';
  }

  setupDrag() {
    if (!this.track) return;
    this.scroller = new Scroller(this.track, { onScroll: () => {} });
    const { signal } = this.lifetimeAbortController;
    this.track.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.track.addEventListener('click', this.handleTrackClick, { signal });
    this.track.addEventListener('dblclick', this.handleTrackDoubleClick, { signal });
    this.track.addEventListener('touchstart', this.handleTouchStart, { signal, passive: false });
    this.track.addEventListener('touchmove', this.handleTouchMove, { signal, passive: false });
    this.track.addEventListener('touchend', this.handleTouchEnd, { signal });
    this.track.addEventListener('touchcancel', this.handleTouchEnd, { signal });
  }

  /** @param {Event} event */
  handleLightboxOpen = (event) => {
    const detail = /** @type {KbLightboxDetail} */ (/** @type {CustomEvent} */ (event).detail || {});
    const slides = this.collectSlides(detail);
    if (!this.track || slides.length === 0) return;
    this.openAbortController.abort();
    this.openAbortController = new AbortController();
    this.renderSlides(slides);
    this.state.pendingIndex = this.startIndex(detail, slides.length);
    this.queueZoomSources(this.state.pendingIndex);
    this.open(detail.sourceElement instanceof HTMLElement ? detail.sourceElement : null);
  };

  /**
   * @param {number} index
   */
  queueZoomSources(index) {
    if (!this.settings.zoomEnabled || !this.track) return;
    const slide = this.track.children[index];
    const media = slide instanceof HTMLElement ? slide.querySelector('img') : null;
    if (!(media instanceof HTMLImageElement)) return;
    const url = media.getAttribute('src') || '';
    if (!url) return;
    kbImagePreload.add([{ url: this.imageSource.urlAtWidth(url, this.zoomWidth(media)) }]);
  }

  /** @param {HTMLElement|null} sourceElement */
  open(sourceElement) {
    this.zoomSourceElement = sourceElement || null;
    this.zoomSourceRect =
      sourceElement && !this.state.reducedMotion ? sourceElement.getBoundingClientRect() : null;
    this.showDialog();
  }

  handleClosingState() {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;
    const closing = dialog.classList.contains('dialog-closing');
    if (closing && !this.isClosing) {
      this.isClosing = true;
      this.hideGround();
      this.flipToSource();
    } else if (!closing) {
      this.isClosing = false;
    }
  }

  flipToSource() {
    if (this.state.reducedMotion) return;
    this.resetZoom();
    const media = this.activeMedia();
    const sourceElement = this.zoomSourceElement;
    if (!media || !sourceElement || !this.track) return;
    const source = sourceElement.getBoundingClientRect();
    const target = this.track.getBoundingClientRect();
    if (!source.width || !source.height || !target.width || !target.height) return;
    const scale = Math.min(source.width / target.width, source.height / target.height);
    const translateX = source.left + source.width / 2 - (target.left + target.width / 2);
    const translateY = source.top + source.height / 2 - (target.top + target.height / 2);
    media.animate(
      [
        { transform: 'none', opacity: 1 },
        { transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`, opacity: 0 },
      ],
      { duration: this.settings.openDurationMs, easing: this.settings.openEasing, fill: 'forwards' }
    );
  }

  flipFromSource() {
    const source = this.zoomSourceRect;
    this.zoomSourceRect = null;
    const media = source ? this.activeMedia() : null;
    const target = media ? media.getBoundingClientRect() : null;
    if (!source || !media || !target || !target.width || !target.height) {
      this.showGround();
      return;
    }
    const scale = Math.min(source.width / target.width, source.height / target.height);
    const translateX = source.left + source.width / 2 - (target.left + target.width / 2);
    const translateY = source.top + source.height / 2 - (target.top + target.height / 2);
    const animation = media.animate(
      [
        { transform: `translate(${translateX}px, ${translateY}px) scale(${scale})` },
        { transform: 'none' },
      ],
      { duration: this.settings.openDurationMs, easing: this.settings.openEasing }
    );
    animation.finished.then(() => this.showGround()).catch(() => this.showGround());
  }

  showGround() {
    if (this.isClosing || !this.refs.dialog.open) return;
    this.setAttribute(this.dataAttributes.ground, '');
  }

  hideGround() {
    this.removeAttribute(this.dataAttributes.ground);
  }

  handleDialogOpen = () => {
    this.bindVideos();
    this.activate(this.state.pendingIndex, { instant: true });
    this.observeSlides();
    this.flipFromSource();
  };

  handleDialogClose = () => {
    this.openAbortController.abort();
    this.hideGround();
    this.resetZoom();
    this.suppressClick = false;
    this.touch.panning = false;
    this.touch.lastTapTime = 0;
    this.touch.lastTapPosition = null;
    this.zoomSourceElement = null;
    this.isClosing = false;
    this.style.removeProperty('--kb-media-focus-y');
    if (this.slideObserver) {
      this.slideObserver.disconnect();
      this.slideObserver = null;
    }
    this.pauseAllVideos();
    if (this.track) this.track.replaceChildren();
    if (this.segmentsContainer) this.segmentsContainer.replaceChildren();
    this.segments = [];
    this.state.index = 0;
    this.state.count = 0;
    this.state.programmaticIndex = null;
    if (this.scrollSettleTimer) {
      clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = 0;
    }
  };

  /**
   * @param {KbLightboxDetail} detail
   * @returns {HTMLElement[]}
   */
  collectSlides(detail) {
    if (detail.source === 'nib') return this.buildShadeSlides(detail.shadeName || null);
    return this.mirrorGallerySlides();
  }

  /** @returns {HTMLElement[]} */
  mirrorGallerySlides() {
    const scope = this.host || document;
    const gallery = scope.querySelector(this.selectors.gallery);
    if (!gallery) return [];
    this.mirrorMediaFocus(gallery);
    /** @type {HTMLElement[]} */
    const slides = [];
    gallery.querySelectorAll(this.selectors.gallerySlide).forEach((gallerySlide) => {
      if (!(gallerySlide instanceof HTMLElement) || gallerySlide.hidden) return;
      const media = this.cloneGalleryMedia(gallerySlide);
      if (media) slides.push(this.wrapSlide(media));
    });
    return slides;
  }

  /**
   * Carries the gallery's vertical framing into the zoom view. Cloned media loses it on its
   * own: cloneGalleryMedia rewrites className, and the clone leaves the gallery subtree.
   * @param {Element} gallery
   */
  mirrorMediaFocus(gallery) {
    this.applyMediaFocus(
      gallery instanceof HTMLElement ? gallery.style.getPropertyValue('--kb-media-focus-y').trim() : ''
    );
  }

  /** @param {string} value */
  applyMediaFocus(value) {
    if (value) this.style.setProperty('--kb-media-focus-y', value);
    else this.style.removeProperty('--kb-media-focus-y');
  }

  /**
   * Framing for shade slides, which are built from JSON rather than cloned from the gallery.
   * The builder carries its own map on this element; on the PDP the gallery next door owns it.
   * @param {string|null} cartridgeType
   * @returns {string}
   */
  focusFor(cartridgeType) {
    const key = typeof cartridgeType === 'string' ? cartridgeType.trim().toLowerCase() : '';
    if (!key) return '';
    const gallery = this.host ? this.host.querySelector(this.selectors.gallery) : null;
    for (const source of [this, gallery]) {
      if (!(source instanceof HTMLElement)) continue;
      const fallback = source.getAttribute('data-focus-default');
      if (fallback === null) continue;
      return `${source.getAttribute(`data-focus-${key}`) ?? fallback}%`;
    }
    return '';
  }

  /**
   * @param {string|null} shadeName
   * @returns {string|null}
   */
  shadeCartridgeType(shadeName) {
    const host = this.host;
    const data = host && host.data;
    if (!data || !shadeName) return null;
    const shade = this.findShade(data, host ? host.selection : null, shadeName);
    return shade && typeof shade.cartridgeType === 'string' ? shade.cartridgeType : null;
  }

  /**
   * @param {HTMLElement} gallerySlide
   * @returns {HTMLElement|null}
   */
  cloneGalleryMedia(gallerySlide) {
    const video = gallerySlide.querySelector(this.selectors.galleryVideo);
    if (video instanceof HTMLVideoElement) {
      const clone = /** @type {HTMLVideoElement} */ (video.cloneNode(true));
      clone.className = this.classNames.media;
      clone.muted = true;
      clone.controls = false;
      clone.autoplay = false;
      clone.setAttribute('playsinline', '');
      const deferredPoster = clone.dataset.poster;
      if (deferredPoster && !clone.poster) {
        clone.poster = deferredPoster;
        delete clone.dataset.poster;
      }
      return clone;
    }
    const image = gallerySlide.querySelector(this.selectors.galleryImage);
    if (image instanceof HTMLImageElement) {
      const clone = /** @type {HTMLImageElement} */ (image.cloneNode(true));
      clone.className = this.classNames.media;
      clone.loading = 'eager';
      clone.removeAttribute('fetchpriority');
      const masterWidth = gallerySlide.dataset.masterWidth || image.dataset.masterWidth || '';
      if (masterWidth) clone.dataset.masterWidth = masterWidth;
      return clone;
    }
    const fallback = Array.from(gallerySlide.children).find(
      (child) => child instanceof HTMLElement && !child.classList.contains(this.classNames.galleryVideoToggle)
    );
    if (fallback instanceof HTMLElement) {
      const clone = /** @type {HTMLElement} */ (fallback.cloneNode(true));
      clone.classList.add(this.classNames.media);
      return clone;
    }
    return null;
  }

  /**
   * @param {string|null} shadeName
   * @returns {HTMLElement[]}
   */
  buildShadeSlides(shadeName) {
    this.applyMediaFocus(this.focusFor(this.shadeCartridgeType(shadeName)));
    return this.shadeGallery(shadeName).map((entry) => {
      const variant = this.resolveMediaVariant(entry) || { type: 'image', source: null, poster: null, sources: [] };
      const alt = entry.alt || shadeName || '';
      const media = variant.type === 'video' ? this.buildShadeVideo(variant) : this.buildShadeImage(variant, alt);
      return this.wrapSlide(media);
    });
  }

  /**
   * @param {KbShadeMedia|null} entry
   * @returns {KbMediaVariant|null}
   */
  resolveMediaVariant(entry) {
    if (!entry) return null;
    const desktop = window.matchMedia(this.settings.desktopMediaQuery).matches;
    const primary = desktop ? entry.desktop : entry.mobile;
    const fallback = desktop ? entry.mobile : entry.desktop;
    if (this.variantHasMedia(primary)) return primary || null;
    if (this.variantHasMedia(fallback)) return fallback || null;
    return null;
  }

  /**
   * @param {KbMediaVariant|null|undefined} variant
   * @returns {boolean}
   */
  variantHasMedia(variant) {
    if (!variant) return false;
    if (variant.source && variant.source.url) return true;
    return Boolean(variant.poster || (variant.sources && variant.sources.length));
  }

  /**
   * @param {KbMediaVariant} variant
   * @param {string} alt
   * @returns {HTMLImageElement}
   */
  buildShadeImage(variant, alt) {
    const image = document.createElement('img');
    image.className = this.classNames.media;
    image.alt = alt;
    image.loading = 'eager';
    this.imageSource.apply(image, variant.source, '100vw');
    return image;
  }

  /**
   * @param {KbMediaVariant} variant
   * @returns {HTMLVideoElement}
   */
  buildShadeVideo(variant) {
    const video = document.createElement('video');
    video.className = this.classNames.media;
    video.muted = true;
    video.preload = 'metadata';
    video.setAttribute('playsinline', '');
    if (variant.poster) video.poster = variant.poster;
    (variant.sources || []).forEach((source) => {
      const element = document.createElement('source');
      element.src = source.url;
      if (source.type) element.type = source.type;
      video.appendChild(element);
    });
    return video;
  }

  /**
   * @param {string|null} shadeName
   * @returns {KbShadeMedia[]}
   */
  shadeGallery(shadeName) {
    const host = this.host;
    const data = host && host.data;
    if (!data || !shadeName) return [];
    const shade = this.findShade(data, host ? host.selection : null, shadeName);
    if (!shade) return [];
    const viewport = window.matchMedia(this.settings.desktopMediaQuery).matches ? 'desktop' : 'mobile';
    const gallery = Array.isArray(shade.gallery)
      ? shade.gallery.filter(
          (/** @type {KbShadeMedia} */ entry) =>
            (!entry.viewport || entry.viewport === viewport) && this.resolveMediaVariant(entry)
        )
      : [];
    if (gallery.length > 0) return gallery;
    if (shade.modelImage) {
      return [{ desktop: { type: 'image', source: shade.modelImage, poster: null, sources: [] }, mobile: null, alt: shadeName }];
    }
    return [];
  }

  /**
   * @param {any} data
   * @param {any} selection
   * @param {string} shadeName
   * @returns {any}
   */
  findShade(data, selection, shadeName) {
    /** @type {any[][]} */
    const pools = [];
    const cartridges = Array.isArray(data.cartridges) ? data.cartridges : [];
    const selectedCartridgeId = selection ? selection.cartridgeProductId : null;
    const selectedCartridge = cartridges.find((/** @type {any} */ cartridge) =>
      this.matchesId(cartridge.id, selectedCartridgeId)
    );
    if (selectedCartridge && Array.isArray(selectedCartridge.shades)) pools.push(selectedCartridge.shades);
    cartridges.forEach((/** @type {any} */ cartridge) => {
      if (cartridge !== selectedCartridge && Array.isArray(cartridge.shades)) pools.push(cartridge.shades);
    });
    if (Array.isArray(data.shades)) pools.push(data.shades);
    for (const pool of pools) {
      const match = pool.find((shade) => shade && shade.name === shadeName);
      if (match) return match;
    }
    return null;
  }

  /**
   * @param {HTMLElement} media
   * @returns {HTMLElement}
   */
  wrapSlide(media) {
    const slide = document.createElement('div');
    slide.className = this.classNames.slide;
    slide.setAttribute(this.dataAttributes.slide, '');
    slide.appendChild(media);
    if (media instanceof HTMLVideoElement) {
      slide.classList.add(this.classNames.videoSlide);
      slide.classList.add(this.classNames.videoPaused);
      const toggle = this.cloneTemplate(this.selectors.videoToggleTemplate);
      if (toggle) slide.appendChild(toggle);
    }
    return slide;
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement|null}
   */
  cloneTemplate(selector) {
    const template = this.querySelector(selector);
    if (!(template instanceof HTMLTemplateElement)) return null;
    const node = template.content.firstElementChild;
    if (!node) return null;
    const clone = node.cloneNode(true);
    return clone instanceof HTMLElement ? clone : null;
  }

  /** @param {HTMLElement[]} slides */
  renderSlides(slides) {
    if (!this.track) return;
    this.track.replaceChildren(...slides);
    this.state.count = slides.length;
    this.buildSegments();
    if (this.controls) this.controls.hidden = slides.length <= 1;
  }

  buildSegments() {
    if (!this.segmentsContainer || !this.track) return;
    /** @type {HTMLElement[]} */
    const segments = [];
    const total = this.track.children.length;
    Array.from(this.track.children).forEach((slide, position) => {
      const segment = document.createElement('button');
      segment.type = 'button';
      segment.className = this.classNames.segment;
      segment.setAttribute('aria-label', this.segmentLabel(position, total));
      segment.dataset[this.dataAttributes.segmentIndex] = String(position);
      const glyph = slide.querySelector(this.selectors.trackVideo) ? this.cloneTemplate(this.selectors.videoGlyphTemplate) : null;
      if (glyph) {
        segment.classList.add(this.classNames.videoSegment);
        segment.appendChild(glyph);
      } else {
        const line = document.createElement('span');
        line.className = this.classNames.segmentLine;
        const fill = document.createElement('span');
        fill.className = this.classNames.segmentFill;
        line.appendChild(fill);
        segment.appendChild(line);
      }
      segments.push(segment);
    });
    this.segments = segments;
    this.segmentsContainer.replaceChildren(...segments);
  }

  /**
   * @param {number} index
   * @param {number} total
   * @returns {string}
   */
  segmentLabel(index, total) {
    const template = this.segmentsContainer?.dataset.kbSegmentLabel || 'Slide %1 of %2';
    return template.replace('%1', String(index + 1)).replace('%2', String(total));
  }

  bindVideos() {
    if (!this.track) return;
    const { signal } = this.openAbortController;
    this.track.querySelectorAll(this.selectors.trackVideo).forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      video.addEventListener('play', this.handleVideoPlayState, { signal });
      video.addEventListener('pause', this.handleVideoPlayState, { signal });
    });
  }

  /** @param {Event} event */
  handleVideoPlayState = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const slide = video.closest(this.selectors.slide);
    if (slide instanceof HTMLElement) slide.classList.toggle(this.classNames.videoPaused, video.paused);
  };

  /** @param {Event} event */
  handleSegmentsClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest(`.${this.classNames.segment}`) : null;
    if (!(target instanceof HTMLElement)) return;
    const index = Number(target.dataset[this.dataAttributes.segmentIndex]);
    if (Number.isNaN(index)) return;
    this.activate(index, { instant: false });
  };

  /** @param {MouseEvent} event */
  handleTrackClick = (event) => {
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slide = target.closest(this.selectors.slide);
    if (!(slide instanceof HTMLElement)) return;
    const video = slide.querySelector(this.selectors.trackVideo);
    if (video instanceof HTMLVideoElement) {
      this.toggleVideo(video);
      return;
    }
    if (this.settings.zoomEnabled && this.settings.zoomTrigger === 'single' && !this.viaTouch) {
      this.toggleZoomAt(event.clientX, event.clientY);
    }
  };

  /** @param {MouseEvent} event */
  handleTrackDoubleClick = (event) => {
    if (!this.settings.zoomEnabled || this.settings.zoomTrigger !== 'double' || this.viaTouch) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slide = target.closest(this.selectors.slide);
    if (!(slide instanceof HTMLElement)) return;
    if (slide.querySelector(this.selectors.trackVideo)) return;
    this.toggleZoomAt(event.clientX, event.clientY);
  };

  /** @param {HTMLVideoElement} video */
  toggleVideo(video) {
    if (video.paused) {
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    } else {
      video.pause();
    }
  }

  /** @returns {HTMLElement|null} */
  activeMedia() {
    if (!this.track) return null;
    const slide = this.track.children[this.state.index];
    if (!(slide instanceof HTMLElement)) return null;
    const media = slide.querySelector(this.selectors.media);
    return media instanceof HTMLElement ? media : null;
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  toggleZoomAt(clientX, clientY) {
    if (this.state.zoomed) {
      this.resetZoom();
      return;
    }
    this.zoomInAt(clientX, clientY);
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  zoomInAt(clientX, clientY) {
    const media = this.activeMedia();
    if (!media || !this.track || media instanceof HTMLVideoElement) return;
    const trackRectangle = this.track.getBoundingClientRect();
    const centerX = trackRectangle.left + trackRectangle.width / 2;
    const centerY = trackRectangle.top + trackRectangle.height / 2;
    const previousScale = this.state.scale;
    this.state.scale = this.settings.zoomLevel;
    const scaleDelta = this.state.scale / previousScale - 1;
    this.state.translate.x -= ((clientX - centerX) * scaleDelta) / this.state.scale;
    this.state.translate.y -= ((clientY - centerY) * scaleDelta) / this.state.scale;
    this.constrainTranslate();
    this.state.zoomed = true;
    this.setAttribute(this.dataAttributes.zoomed, '');
    this.loadHighResolution(media);
    this.applyZoom(media);
  }

  resetZoom() {
    const media = this.activeMedia();
    this.state.zoomed = false;
    this.state.scale = 1;
    this.state.translate = { x: 0, y: 0 };
    this.removeAttribute(this.dataAttributes.zoomed);
    this.removeAttribute(this.dataAttributes.panning);
    if (media) {
      media.style.removeProperty(this.cssProperties.zoomScale);
      media.style.removeProperty(this.cssProperties.zoomTranslateX);
      media.style.removeProperty(this.cssProperties.zoomTranslateY);
      media.style.removeProperty('--kb-media-focus-y');
    }
  }

  /** @param {HTMLElement} media */
  applyZoom(media) {
    media.style.setProperty(this.cssProperties.zoomScale, String(this.state.scale));
    media.style.setProperty(this.cssProperties.zoomTranslateX, `${this.state.translate.x}px`);
    media.style.setProperty(this.cssProperties.zoomTranslateY, `${this.state.translate.y}px`);
  }

  constrainTranslate() {
    if (!this.track) return;
    if (this.state.scale <= 1) {
      this.state.translate = { x: 0, y: 0 };
      return;
    }
    const trackRectangle = this.track.getBoundingClientRect();
    const maxX = (trackRectangle.width * (this.state.scale - 1)) / 2 / this.state.scale;
    const maxY = (trackRectangle.height * (this.state.scale - 1)) / 2 / this.state.scale;
    this.state.translate.x = Math.max(-maxX, Math.min(maxX, this.state.translate.x));
    this.state.translate.y = Math.max(-maxY, Math.min(maxY, this.state.translate.y));
  }

  /**
   * Vertical geometry of the cover crop, in unscaled pixels. Null when the source is no taller
   * than the viewer, where cover crops sideways instead and object-position has no vertical
   * slack to give, and below 990 where the object-position rule is not declared at all.
   * @param {HTMLElement} media
   * @returns {{ box: number, overflow: number }|null}
   */
  verticalCoverGeometry(media) {
    if (!this.track || !(media instanceof HTMLImageElement)) return null;
    if (!window.matchMedia(this.settings.desktopMediaQuery).matches) return null;
    if (!media.naturalWidth || !media.naturalHeight) return null;
    const rectangle = this.track.getBoundingClientRect();
    if (!rectangle.width || !rectangle.height) return null;
    const content = (rectangle.width * media.naturalHeight) / media.naturalWidth;
    const overflow = content - rectangle.height;
    if (overflow <= 1) return null;
    return { box: rectangle.height, overflow };
  }

  /**
   * Slack the transform alone can pan on one side, in unscaled pixels. Same bound
   * constrainTranslate already applies to the vertical axis.
   * @param {number} box
   * @returns {number}
   */
  verticalPanSlack(box) {
    return (box - box / this.state.scale) / 2;
  }

  /**
   * Top of the visible slice, measured in unscaled pixels down the whole photograph.
   * @param {{ box: number, overflow: number }} geometry
   * @param {number} focusPercent
   * @param {number} translateY
   * @returns {number}
   */
  verticalPanOffset(geometry, focusPercent, translateY) {
    return this.verticalPanSlack(geometry.box) - translateY + (geometry.overflow * focusPercent) / 100;
  }

  /**
   * Takes the pan past the cover crop. The transform can only travel within the band already on
   * screen, so once its slack runs out the rest is spent on object-position, which is what
   * decides how much of the source the crop keeps. Together they reach the whole photograph.
   * @param {HTMLElement} media
   * @param {{ box: number, overflow: number }} geometry
   * @param {number} offset
   */
  applyVerticalPan(media, geometry, offset) {
    const slack = this.verticalPanSlack(geometry.box);
    const clamped = Math.max(0, Math.min(slack * 2 + geometry.overflow, offset));
    const percent = Math.max(0, Math.min(100, ((clamped - slack) / geometry.overflow) * 100));
    this.state.translate.y = slack + (geometry.overflow * percent) / 100 - clamped;
    media.style.setProperty('--kb-media-focus-y', `${percent}%`);
  }

  /**
   * @param {HTMLElement} media
   * @returns {number}
   */
  focusPercentOf(media) {
    const raw = getComputedStyle(media).getPropertyValue('--kb-media-focus-y').trim();
    const percent = raw.endsWith('%') ? Number.parseFloat(raw) : NaN;
    return Number.isFinite(percent) ? percent : 50;
  }

  /** @param {HTMLElement} media */
  loadHighResolution(media) {
    if (!(media instanceof HTMLImageElement)) return;
    if (media.dataset[this.dataAttributes.highResolution] === 'true') return;
    const source = media.currentSrc || media.src;
    if (!source) return;
    media.src = this.imageSource.urlAtWidth(source, this.zoomWidth(media));
    media.removeAttribute('srcset');
    media.removeAttribute('sizes');
    media.dataset[this.dataAttributes.highResolution] = 'true';
  }

  /**
   * @param {HTMLImageElement} media
   * @returns {number}
   */
  zoomWidth(media) {
    const devicePixels = window.innerWidth * (window.devicePixelRatio || 1);
    return this.imageSource.zoomWidth(
      this.imageSource.masterWidthOf(media),
      devicePixels,
      this.settings.zoomLevel
    );
  }

  /** @param {PointerEvent} event */
  handlePointerDown = (event) => {
    if (event.button !== 0 || event.pointerType === 'touch') return;
    this.viaTouch = false;
    if (this.state.zoomed) {
      this.startDesktopPan(event);
      return;
    }
    if (!this.scroller || this.dragging) return;
    if (this.state.count <= 1) return;
    this.suppressClick = false;
    event.preventDefault();

    const scroller = this.scroller;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startTime = performance.now();
    const controller = new AbortController();
    const { signal } = controller;
    let previous = startX;
    let velocity = 0;
    let moved = false;
    let distance = 0;
    this.dragging = true;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      const current = moveEvent.clientX;
      if (!moved) {
        if (current === startX) return;
        moved = true;
        try {
          this.track?.setPointerCapture(pointerId);
        } catch {}
        this.setAttribute(this.dataAttributes.dragging, '');
        scroller.snap = false;
      }
      const delta = previous - current;
      const elapsed = performance.now() - startTime;
      if (elapsed > 0) velocity = Math.round((delta / elapsed) * 1000);
      previous = current;
      distance += Math.abs(delta);
      scroller.by(delta, { instant: true });
    };

    const onPointerUp = async () => {
      controller.abort();
      this.dragging = false;
      try {
        this.track?.releasePointerCapture(pointerId);
      } catch {}
      this.removeAttribute(this.dataAttributes.dragging);
      if (!moved) return;
      this.suppressClick = true;
      const direction = Math.sign(velocity);
      const slow =
        Math.abs(velocity) < this.settings.dragThreshold || distance < this.settings.dragThreshold;
      const target = Math.max(0, Math.min(this.state.count - 1, this.nearestIndex() + (slow ? 0 : direction)));
      const slide = this.track ? this.track.children[target] : null;
      if (slide instanceof HTMLElement) scroller.to(slide);
      await scroller.finished;
      if (this.dragging) return;
      scroller.snap = true;
      this.state.index = target;
      this.renderSegmentStates(target);
      this.pauseInactiveVideos(target);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  /** @param {PointerEvent} event */
  startDesktopPan(event) {
    const media = this.activeMedia();
    if (!media) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startTranslateX = this.state.translate.x;
    const startTranslateY = this.state.translate.y;
    const geometry = this.verticalCoverGeometry(media);
    const startOffset = geometry
      ? this.verticalPanOffset(geometry, this.focusPercentOf(media), startTranslateY)
      : 0;
    const controller = new AbortController();
    const { signal } = controller;
    let moved = false;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      moved = true;
      this.suppressClick = true;
      this.setAttribute(this.dataAttributes.panning, '');
      this.state.translate.x = startTranslateX + (moveEvent.clientX - startX) / this.state.scale;
      const verticalDelta = (moveEvent.clientY - startY) / this.state.scale;
      if (geometry) this.applyVerticalPan(media, geometry, startOffset - verticalDelta);
      else this.state.translate.y = startTranslateY + verticalDelta;
      this.constrainTranslate();
      this.applyZoom(media);
    };

    const onPointerUp = () => {
      controller.abort();
      this.removeAttribute(this.dataAttributes.panning);
      if (!moved) this.suppressClick = false;
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  }

  /** @param {TouchEvent} event */
  handleTouchStart = (event) => {
    this.viaTouch = true;
    if (!this.settings.zoomEnabled) return;
    const touches = event.touches;
    if (touches.length === 2) {
      const first = touches[0];
      const second = touches[1];
      if (!first || !second) return;
      event.preventDefault();
      this.touch.initialDistance = pointerDistance(first, second) || 1;
      this.touch.startScale = this.state.scale;
      this.touch.panning = false;
      this.touch.moved = true;
      this.setAttribute(this.dataAttributes.panning, '');
      return;
    }
    if (touches.length !== 1) return;
    const touch = touches[0];
    if (!touch) return;
    const now = performance.now();
    if (
      this.touch.lastTapPosition &&
      now - this.touch.lastTapTime < this.settings.doubleTapDelayMs &&
      pointerDistance(touch, this.touch.lastTapPosition) < this.settings.doubleTapDistance
    ) {
      event.preventDefault();
      this.toggleZoomAt(touch.clientX, touch.clientY);
      this.touch.lastTapTime = 0;
      this.touch.lastTapPosition = null;
      return;
    }
    this.touch.gestureStart = { x: touch.clientX, y: touch.clientY };
    this.touch.gestureStartTime = now;
    this.touch.moved = false;
    if (this.state.zoomed) {
      event.preventDefault();
      this.touch.panning = true;
      this.touch.startPosition = { x: touch.clientX, y: touch.clientY };
      this.touch.startTranslate = { x: this.state.translate.x, y: this.state.translate.y };
      this.setAttribute(this.dataAttributes.panning, '');
    }
  };

  /** @param {TouchEvent} event */
  handleTouchMove = (event) => {
    if (!this.settings.zoomEnabled) return;
    const touches = event.touches;
    if (touches.length === 2) {
      const first = touches[0];
      const second = touches[1];
      if (!first || !second) return;
      event.preventDefault();
      this.processPinch(first, second);
      return;
    }
    if (touches.length !== 1) return;
    const touch = touches[0];
    if (!touch) return;
    if (!this.touch.moved && pointerDistance(touch, this.touch.gestureStart) > this.settings.dragThreshold) {
      this.touch.moved = true;
    }
    if (this.state.zoomed && this.touch.panning) {
      event.preventDefault();
      const media = this.activeMedia();
      if (!media) return;
      this.state.translate.x = this.touch.startTranslate.x + (touch.clientX - this.touch.startPosition.x) / this.state.scale;
      this.state.translate.y = this.touch.startTranslate.y + (touch.clientY - this.touch.startPosition.y) / this.state.scale;
      this.constrainTranslate();
      this.applyZoom(media);
    }
  };

  /** @param {TouchEvent} event */
  handleTouchEnd = (event) => {
    if (event.touches.length === 1 && this.state.zoomed) {
      const touch = event.touches[0];
      if (touch) {
        this.touch.panning = true;
        this.touch.moved = true;
        this.touch.startPosition = { x: touch.clientX, y: touch.clientY };
        this.touch.startTranslate = { x: this.state.translate.x, y: this.state.translate.y };
        this.setAttribute(this.dataAttributes.panning, '');
      }
      return;
    }
    if (event.touches.length > 0) return;
    const isStationaryTap =
      event.type === 'touchend' &&
      !this.touch.moved &&
      performance.now() - this.touch.gestureStartTime < this.settings.doubleTapDelayMs;
    if (isStationaryTap) {
      this.touch.lastTapTime = performance.now();
      this.touch.lastTapPosition = { x: this.touch.gestureStart.x, y: this.touch.gestureStart.y };
    } else {
      this.touch.lastTapTime = 0;
      this.touch.lastTapPosition = null;
    }
    this.touch.panning = false;
    this.removeAttribute(this.dataAttributes.panning);
    if (this.state.scale <= 1) {
      this.resetZoom();
    } else {
      const media = this.activeMedia();
      if (media) this.loadHighResolution(media);
    }
  };

  /**
   * @param {Touch} first
   * @param {Touch} second
   */
  processPinch(first, second) {
    const media = this.activeMedia();
    if (!media || !this.track || media instanceof HTMLVideoElement) return;
    const midpointX = (first.clientX + second.clientX) / 2;
    const midpointY = (first.clientY + second.clientY) / 2;
    const currentDistance = pointerDistance(first, second);
    const previousScale = this.state.scale;
    const nextScale = (currentDistance / this.touch.initialDistance) * this.touch.startScale;
    this.state.scale = Math.max(1, Math.min(this.settings.zoomMax, nextScale));
    const trackRectangle = this.track.getBoundingClientRect();
    const distanceFromCenterX = midpointX - (trackRectangle.left + trackRectangle.width / 2);
    const distanceFromCenterY = midpointY - (trackRectangle.top + trackRectangle.height / 2);
    const scaleDelta = this.state.scale / previousScale - 1;
    this.state.translate.x -= (distanceFromCenterX * scaleDelta) / this.state.scale;
    this.state.translate.y -= (distanceFromCenterY * scaleDelta) / this.state.scale;
    this.constrainTranslate();
    this.state.zoomed = this.state.scale > 1;
    if (this.state.zoomed) {
      this.setAttribute(this.dataAttributes.zoomed, '');
    } else {
      this.removeAttribute(this.dataAttributes.zoomed);
    }
    this.applyZoom(media);
  }

  /** @returns {number} */
  nearestIndex() {
    if (!this.track) return this.state.index;
    const scrollStart = this.track.scrollLeft;
    let best = 0;
    let bestDistance = Infinity;
    Array.from(this.track.children).forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;
      const distance = Math.abs(slide.offsetLeft - scrollStart);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  activate(index, options = {}) {
    if (!this.track || this.state.count === 0) return;
    if (this.state.zoomed) this.resetZoom();
    const clamped = Math.max(0, Math.min(this.state.count - 1, index));
    const previous = this.state.index;
    this.state.index = clamped;
    const slide = this.track.children[clamped];
    if (slide instanceof HTMLElement) {
      const behavior = options.instant || this.state.reducedMotion ? 'auto' : 'smooth';
      if (behavior === 'smooth' && clamped !== previous) this.beginProgrammaticScroll(clamped);
      this.track.scrollTo({ left: slide.offsetLeft, behavior });
    }
    this.pauseInactiveVideos(clamped);
    this.renderSegmentStates(clamped);
    this.queueZoomSources(clamped);
  }

  /** @param {number} activeIndex */
  pauseInactiveVideos(activeIndex) {
    if (!this.track) return;
    Array.from(this.track.children).forEach((slide, position) => {
      if (position === activeIndex) return;
      const video = slide.querySelector(this.selectors.trackVideo);
      if (video instanceof HTMLVideoElement) video.pause();
    });
  }

  pauseAllVideos() {
    if (!this.track) return;
    this.track.querySelectorAll(this.selectors.trackVideo).forEach((video) => {
      if (video instanceof HTMLVideoElement) video.pause();
    });
  }

  /** @param {number} index */
  beginProgrammaticScroll(index) {
    this.state.programmaticIndex = index;
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = window.setTimeout(() => {
      this.state.programmaticIndex = null;
      this.scrollSettleTimer = 0;
    }, this.settings.programmaticScrollMs);
  }

  /** @param {number} index */
  renderSegmentStates(index) {
    this.segments.forEach((segment, position) => {
      segment.classList.toggle(this.classNames.activeSegment, position === index);
      if (position === index) segment.setAttribute('aria-current', 'true');
      else segment.removeAttribute('aria-current');
    });
  }

  observeSlides() {
    if (!this.track) return;
    if (this.slideObserver) this.slideObserver.disconnect();
    this.slideObserver = new IntersectionObserver(this.handleSlideIntersect, {
      root: this.track,
      threshold: this.settings.intersectionThresholds,
    });
    Array.from(this.track.children).forEach((slide) => {
      if (slide instanceof HTMLElement) this.slideObserver?.observe(slide);
    });
  }

  /** @param {IntersectionObserverEntry[]} entries */
  handleSlideIntersect = (entries) => {
    if (this.state.zoomed) return;
    /** @type {Element|null} */
    let best = null;
    let bestRatio = this.settings.activeThreshold;
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= bestRatio) {
        bestRatio = entry.intersectionRatio;
        best = entry.target;
      }
    }
    if (!(best instanceof HTMLElement) || !this.track) return;
    const index = Array.from(this.track.children).indexOf(best);
    if (index === -1) return;
    if (this.state.programmaticIndex !== null) {
      if (index !== this.state.programmaticIndex) return;
      this.state.programmaticIndex = null;
      if (this.scrollSettleTimer) {
        clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = 0;
      }
    }
    if (index === this.state.index) return;
    this.state.index = index;
    this.pauseInactiveVideos(index);
    this.renderSegmentStates(index);
    this.queueZoomSources(index);
  };

  /**
   * @param {KbLightboxDetail} detail
   * @param {number} count
   * @returns {number}
   */
  startIndex(detail, count) {
    const index = Number(detail.index);
    if (Number.isNaN(index)) return 0;
    return Math.max(0, Math.min(count - 1, index));
  }

  /**
   * @param {number|string|null|undefined} first
   * @param {number|string|null|undefined} second
   * @returns {boolean}
   */
  matchesId(first, second) {
    return first != null && second != null && String(first) === String(second);
  }
}

/**
 * @param {KbPoint|Touch} first
 * @param {KbPoint|Touch} second
 * @returns {number}
 */
function pointerDistance(first, second) {
  const firstX = /** @type {any} */ (first).x ?? /** @type {Touch} */ (first).clientX;
  const firstY = /** @type {any} */ (first).y ?? /** @type {Touch} */ (first).clientY;
  const secondX = /** @type {any} */ (second).x ?? /** @type {Touch} */ (second).clientX;
  const secondY = /** @type {any} */ (second).y ?? /** @type {Touch} */ (second).clientY;
  const deltaX = firstX - secondX;
  const deltaY = firstY - secondY;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}

if (!customElements.get('kb-pdp-lightbox')) {
  customElements.define('kb-pdp-lightbox', KbPdpLightbox);
}

export { KbPdpLightbox };
